import { ref, computed, onBeforeUnmount } from 'vue';
import axios from 'axios';
import { promptChainApi } from '@/services/promptChainApi';
import { executionPolling } from '@/utils/pollingService';

export function useSkillsAiWorkflows() {
  // 1. Create self with reactive state
  const self = {
    panelOpen: ref(false),
    loading: ref(false),
    chains: ref([]),
    selectedChainId: ref(null),
    selectedSkills: ref([]),
    formattedPayload: ref(null),

    // Execution state
    executionState: ref('idle'),       // 'idle' | 'executing' | 'completed' | 'failed'
    executionId: ref(null),
    executionProgress: ref(null),      // { progress_percentage, completed_steps, total_steps, failed_steps }
    executionCurrentStep: ref(null),
    executionError: ref(null),
    executionResult: ref(null),        // { outputs, executionDetails }
    executionStartedAt: ref(null),
    elapsedSeconds: ref(0),
  };

  // Private timer handle
  let _elapsedTimer = null;

  // 2. Computed properties on self
  self.selectedChain = computed(() => {
    if (!self.selectedChainId.value) return null;
    return self.chains.value.find(c => c.prompt_chain_id === self.selectedChainId.value) || null;
  });

  self.inputSchemaInputs = computed(() => {
    const chain = self.selectedChain.value;
    if (!chain?.input_schema?.inputs) return [];
    return chain.input_schema.inputs;
  });

  self.parsedSkillResults = computed(() => {
    const result = self.executionResult.value;
    if (!result?.outputs) return [];

    // The chain output may have a 'result' key containing the skills array,
    // or the skills may be directly in the outputs under various keys
    const outputs = result.outputs;

    // Try common output locations
    let skills = outputs.result || outputs.skills || outputs.Skills || null;

    // If result is a string, try to parse it
    if (typeof skills === 'string') {
      try { skills = JSON.parse(skills); } catch { skills = null; }
    }

    // If result is an object with a nested result/skills array, unwrap it
    // e.g. { reason: "...", result: [{ Id, Description, ... }] }
    if (skills && !Array.isArray(skills) && typeof skills === 'object') {
      skills = skills.result || skills.skills || skills.Skills || null;
    }

    // If the unwrapped value is a string, try parsing it too
    if (typeof skills === 'string') {
      try { skills = JSON.parse(skills); } catch { skills = null; }
    }

    if (!Array.isArray(skills)) return [];
    return skills;
  });

  // 3. Private helpers
  const PROFICIENCY_ORDER = ['Basic', 'Intermediate', 'Advanced', 'Expert', 'Strategic'];

  function _sortProficiencies(items) {
    return [...items].sort((a, b) => {
      const aIdx = PROFICIENCY_ORDER.indexOf(a.proficiency);
      const bIdx = PROFICIENCY_ORDER.indexOf(b.proficiency);
      // Unknown levels go to the end
      return (aIdx === -1 ? 999 : aIdx) - (bIdx === -1 ? 999 : bIdx);
    });
  }

  function _formatSkillsPayload(skills, inputDefs) {
    const arrayInput = inputDefs.find(i =>
      i.data_type === 'array' || i.key?.toLowerCase().includes('skill')
    );
    const inputKey = arrayInput?.key || 'Skills';

    const formatted = skills.map(skill => ({
      Id: skill.external_id,
      Description: skill.name,
      Type: skill.type,
    }));

    return {
      inputKey,
      items: formatted,
      payload: { [inputKey]: formatted }
    };
  }

  // Methods exposed on self for template use
  self.getSkillDescriptor = (skill) => {
    if (!skill.descriptors) return skill.descriptor || '';
    if (typeof skill.descriptors === 'string') return skill.descriptors;
    // Prefer en-US, then en-GB, then first available
    const val = skill.descriptors['en-US'] || skill.descriptors['en-GB'] || Object.values(skill.descriptors)[0];
    if (!val) return '';
    // Value may be a string directly or an object with a descriptor property
    return typeof val === 'string' ? val : (val.descriptor || '');
  };

  self.getSkillProficiencies = (skill) => {
    if (!skill.proficiencies) return [];
    // If proficiencies is already an array of { proficiency, descriptor }
    if (Array.isArray(skill.proficiencies)) return _sortProficiencies(skill.proficiencies);
    // Pick language: en-US > en-GB > first available
    const lang = skill.proficiencies['en-US'] || skill.proficiencies['en-GB'] || Object.values(skill.proficiencies)[0];
    if (!lang) return [];
    if (Array.isArray(lang)) return _sortProficiencies(lang);
    // If lang is an object keyed by level name, e.g. { Basic: "...", Intermediate: "..." }
    if (typeof lang === 'object') {
      const items = Object.entries(lang).map(([proficiency, descriptor]) => ({ proficiency, descriptor }));
      return _sortProficiencies(items);
    }
    return [];
  };

  function _autoSelectDefaultChain() {
    const match = self.chains.value.find(c =>
      c.chain_name?.toLowerCase().includes('skill proficiency')
    );
    self.selectedChainId.value = match
      ? match.prompt_chain_id
      : (self.chains.value[0]?.prompt_chain_id || null);
  }

  function _startElapsedTimer() {
    _stopElapsedTimer();
    self.elapsedSeconds.value = 0;
    _elapsedTimer = setInterval(() => {
      self.elapsedSeconds.value++;
    }, 1000);
  }

  function _stopElapsedTimer() {
    if (_elapsedTimer) {
      clearInterval(_elapsedTimer);
      _elapsedTimer = null;
    }
  }

  function _collectOutputs(execution) {
    const allOutputs = {};
    if (execution.steps) {
      execution.steps.forEach(step => {
        if (step.outputs) {
          Object.assign(allOutputs, step.outputs);
        }
      });
    }
    return allOutputs;
  }

  function _handlePollResponse(response) {
    if (!response.data?.success) {
      return {};
    }

    const execution = response.data.data;

    switch (execution.execution_status) {
      case 'queued':
        self.executionCurrentStep.value = 'Queued for generation...';
        self.executionProgress.value = null;
        return {};

      case 'running': {
        if (execution.progress) {
          self.executionProgress.value = {
            progress_percentage: execution.progress.progress_percentage,
            completed_steps: execution.progress.completed_steps,
            total_steps: execution.progress.total_steps,
            failed_steps: execution.progress.failed_steps
          };
        }

        if (execution.current_step && execution.progress) {
          const currentStepNum = (execution.progress.completed_steps || 0) + 1;
          self.executionCurrentStep.value = `${execution.current_step.step_name} (${currentStepNum}/${execution.progress.total_steps})`;
        } else if (execution.current_step) {
          self.executionCurrentStep.value = execution.current_step.step_name;
        } else {
          self.executionCurrentStep.value = 'Processing...';
        }
        return {};
      }

      case 'completed': {
        const outputs = _collectOutputs(execution);
        _stopElapsedTimer();

        self.executionResult.value = {
          outputs,
          executionDetails: {
            chain_name: execution.chain_name,
            total_steps: execution.progress?.total_steps || 0,
            completed_steps: execution.progress?.completed_steps || 0,
            processing_time: execution.completed_at && execution.started_at
              ? new Date(execution.completed_at) - new Date(execution.started_at)
              : null
          }
        };
        self.executionState.value = 'completed';
        return { shouldStop: true, data: outputs };
      }

      case 'failed': {
        _stopElapsedTimer();
        self.executionError.value = execution.error_message || 'Execution failed';
        self.executionState.value = 'failed';
        return { shouldStop: true };
      }

      default:
        return {};
    }
  }

  // 4. Methods on self
  self.fetchChains = async (notify) => {
    self.loading.value = true;
    try {
      const resp = await axios.get('externalContent/availablePromptChains');
      const data = resp.data;
      if (data.success) {
        self.chains.value = data.chains || [];
      } else {
        self.chains.value = [];
        notify?.('Error loading prompt chains', 'error');
      }
    } catch (error) {
      console.error('Error fetching prompt chains', error);
      notify?.('Error loading prompt chains', 'error');
    } finally {
      self.loading.value = false;
    }
  };

  self.openPanel = async (selectedSkills, notify) => {
    self.selectedSkills.value = selectedSkills;
    self.panelOpen.value = true;

    if (!self.chains.value.length) {
      await self.fetchChains(notify);
      _autoSelectDefaultChain();
    }

    self.buildPayload();
  };

  self.closePanel = () => {
    self.panelOpen.value = false;
    self.formattedPayload.value = null;
    if (self.executionState.value === 'executing') {
      // Stop polling if closing during execution
      if (self.executionId.value) {
        executionPolling.stopPolling(self.executionId.value);
      }
      _stopElapsedTimer();
    }
  };

  self.buildPayload = () => {
    if (!self.selectedSkills.value.length) {
      self.formattedPayload.value = null;
      return;
    }

    const inputDefs = self.inputSchemaInputs.value;
    self.formattedPayload.value = _formatSkillsPayload(
      self.selectedSkills.value,
      inputDefs
    );
  };

  self.onChainChange = () => {
    self.buildPayload();
  };

  self.executeChain = async (notify) => {
    if (!self.selectedChainId.value || !self.formattedPayload.value) return;

    // Reset execution state
    self.executionState.value = 'executing';
    self.executionId.value = null;
    self.executionProgress.value = null;
    self.executionCurrentStep.value = 'Initialising...';
    self.executionError.value = null;
    self.executionResult.value = null;
    self.executionStartedAt.value = Date.now();

    _startElapsedTimer();

    try {
      const resp = await promptChainApi.triggerChain({
        prompt_chain_id: self.selectedChainId.value,
        source_data: {
          custom: { [self.formattedPayload.value.inputKey]: self.formattedPayload.value.items } 
        }
      });

      if (!resp.data.success) {
        _stopElapsedTimer();
        self.executionError.value = resp.data.error || 'Failed to start chain execution';
        self.executionState.value = 'failed';
        notify?.('Failed to start execution', 'error');
        return;
      }

      const executionId = resp.data.execution_id;
      self.executionId.value = executionId;

      executionPolling.startPolling(
        executionId,
        async () => {
          const statusResp = await promptChainApi.getExecutionStatus(executionId);
          return _handlePollResponse(statusResp);
        },
        () => {
          // onComplete callback — already handled in _handlePollResponse
          if (self.executionState.value === 'completed') {
            notify?.('Chain execution completed', 'success');
          }
        },
        (error) => {
          console.error('Polling error:', error);
          _stopElapsedTimer();
          self.executionError.value = 'Lost connection while polling for status';
          self.executionState.value = 'failed';
          notify?.('Error monitoring execution', 'error');
        }
      );
    } catch (error) {
      console.error('Error executing chain', error);
      _stopElapsedTimer();
      self.executionError.value = error.response?.data?.error || error.message || 'Failed to execute chain';
      self.executionState.value = 'failed';
      notify?.('Error executing chain', 'error');
    }
  };

  self.resetExecution = () => {
    if (self.executionId.value) {
      executionPolling.stopPolling(self.executionId.value);
    }
    _stopElapsedTimer();
    self.executionState.value = 'idle';
    self.executionId.value = null;
    self.executionProgress.value = null;
    self.executionCurrentStep.value = null;
    self.executionError.value = null;
    self.executionResult.value = null;
    self.executionStartedAt.value = null;
    self.elapsedSeconds.value = 0;
  };

  // 5. Cleanup
  onBeforeUnmount(() => {
    if (self.executionId.value) {
      executionPolling.stopPolling(self.executionId.value);
    }
    _stopElapsedTimer();
  });

  return self;
}
